import { throttle } from './throttle';

const getOffscreenOffset = (boundingRect: {
  top: number;
  right: number;
  bottom: number;
  left: number;
}) => ({
  offscreenX:
    (boundingRect.right < 0 && boundingRect.right) ||
    (boundingRect.left > window.innerWidth &&
      boundingRect.left - window.innerWidth) ||
    0,
  offscreenY:
    (boundingRect.bottom < 0 && boundingRect.bottom) ||
    (boundingRect.top > window.innerHeight &&
      boundingRect.top - window.innerHeight) ||
    0,
});

const inViewportFallback = (element: Element) => {
  const { top, right, bottom, left, width, height } =
    element.getBoundingClientRect();

  const intersection = {
    t: bottom,
    r: window.innerWidth - left,
    b: window.innerHeight - top,
    l: right,
  };

  return (
    intersection.t > height &&
    intersection.r > width &&
    intersection.b > height &&
    intersection.l > width
  );
};

const inViewport = (
  element: Element,
  cb: (result: {
    isVisible: boolean;
    getOffscreenOffset: () => {
      offscreenX: number;
      offscreenY: number;
    };
  }) => void
): { connect: () => void; disconnect: () => void } => {
  let isVisible: boolean;
  let isConnected = false;

  /* Efficient way of tracking supported by most of the Browsers except <IE11 */
  if (typeof IntersectionObserver !== 'undefined') {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting !== isVisible) {
        isVisible = entry.isIntersecting;
        cb({
          isVisible,
          getOffscreenOffset: () =>
            getOffscreenOffset(element.getBoundingClientRect()),
        });
      }
    });
    const connect = () => {
      if (!isConnected) {
        observer.observe(element);
        isConnected = true;
      }
    };
    const disconnect = () => {
      if (isConnected) {
        observer.disconnect();
        isConnected = false;
      }
    };

    connect();

    return {
      connect,
      disconnect,
    };
  } else {
    // Implementation for < IE11
    const triggers = ['scroll', 'resize', 'load'];

    const check = throttle(() => {
      const isInViewport = inViewportFallback(element);

      if (isInViewport !== isVisible) {
        isVisible = isInViewport;
        cb({
          isVisible,
          getOffscreenOffset: () =>
            getOffscreenOffset(element.getBoundingClientRect()),
        });
      }
    }, 200);

    const connect = () => {
      if (!isConnected) {
        triggers.forEach((event) => addEventListener(event, check));
        isConnected = true;
      }
    };
    const disconnect = () => {
      if (isConnected) {
        triggers.forEach((event) => removeEventListener(event, check));
        isConnected = false;
      }
    };

    connect();

    return {
      connect,
      disconnect,
    };
  }
};

export default inViewport;
